export type ParsedChapter = {
  startTime: number;
  endTime: number;
  title: string;
};

function stripCueText(raw: string): string {
  return raw.replace(/<[^>]+>/g, '').trim();
}

/** Parses one WebVTT timestamp (minutes:seconds or hours:minutes:seconds, optional ms). */
export function parseWebVttTimestamp(raw: string): number {
  const token = raw.trim().split(/\s+/)[0] ?? '';
  const parts = token.split(':');
  if (parts.length < 2) {
    const n = Number.parseFloat(token);
    return Number.isFinite(n) ? n : 0;
  }
  const last = parts.at(-1) ?? '0';
  const sec = Number.parseFloat(last);
  if (!Number.isFinite(sec)) return 0;
  if (parts.length === 2) {
    const minutes = Number.parseInt(parts[0], 10);
    return (Number.isFinite(minutes) ? minutes : 0) * 60 + sec;
  }
  const hours = Number.parseInt(parts[0], 10);
  const minutes = Number.parseInt(parts[1], 10);
  return (Number.isFinite(hours) ? hours : 0) * 3600 + (Number.isFinite(minutes) ? minutes : 0) * 60 + sec;
}

/**
 * Parses a WebVTT document into chapter cues (JW Player / HTML5-style chapter tracks).
 * Ignores header lines, NOTE blocks, and STYLE until the first cue.
 */
export function parseChaptersWebVtt(vtt: string): ParsedChapter[] {
  const normalized = vtt.replaceAll('\r\n', '\n').trim();
  if (!normalized.toUpperCase().startsWith('WEBVTT')) {
    return [];
  }

  const blocks = normalized.split(/\n{2,}/);
  const out: ParsedChapter[] = [];

  for (const block of blocks) {
    const lines = block
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) continue;

    let timingIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('-->')) {
        timingIdx = i;
        break;
      }
    }
    if (timingIdx < 0) continue;

    const timingLine = lines[timingIdx];
    const arrowParts = timingLine.split('-->');
    if (arrowParts.length < 2) continue;

    const startTime = parseWebVttTimestamp(arrowParts[0]);
    const endPart = arrowParts[1].trim();
    const endTime = parseWebVttTimestamp(endPart);

    const titleRaw = lines.slice(timingIdx + 1).join('\n');
    const title = stripCueText(titleRaw);

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) continue;
    out.push({ startTime, endTime, title });
  }

  out.sort((a, b) => a.startTime - b.startTime);
  return out;
}
